import { useCallback, useEffect, useState } from 'react'
import {
  WizardFieldSchema,
  WizardSchema,
  WizardStateSchema,
} from '@/lib/definitions/wizard.types'

export interface WizardValidationResult {
  isValid: boolean
  invalidFieldIds: string[]
  firstInvalidFieldId?: string
}

interface UseSchemaWizardReturn<TState extends WizardStateSchema> {
  state: TState
  updateField: (fieldId: string, value: any) => void
  updateFields: (values: Partial<TState>) => void
  resetWizard: () => void
  isFieldValid: (fieldId: string) => boolean
  isWizardValid: () => boolean
  validationAttempted: boolean
  invalidFieldIds: string[]
  validateWithFeedback: () => WizardValidationResult
}

function getRequiredFields<TState extends WizardStateSchema>(
  field: WizardFieldSchema<TState>,
  state: TState
): (keyof TState)[] {
  if (!field.requiredFields) return []
  return typeof field.requiredFields === 'function'
    ? field.requiredFields(state)
    : field.requiredFields
}

function isFieldDisabled<TState extends WizardStateSchema>(
  field: WizardFieldSchema<TState>,
  state: TState
): boolean {
  if (field.disabledWhen === undefined || field.disabledWhen === null) {
    return false
  }

  if (typeof field.disabledWhen === 'function') {
    const result = field.disabledWhen(state)
    if (Array.isArray(result)) {
      return result.some((fieldName) => !state[fieldName])
    }
    return !!result
  }

  return field.disabledWhen.some((fieldName) => !state[fieldName])
}

export function isFieldComplete<TState extends WizardStateSchema>(
  field: WizardFieldSchema<TState>,
  state: TState
): boolean {
  if (!field.requiredFields) return false
  const required = getRequiredFields(field, state)
  if (!required.every((fieldName) => !!state[fieldName])) return false
  return field.completeWhen ? field.completeWhen(state) : true
}

function getInvalidFieldIds<TState extends WizardStateSchema>(
  schema: WizardSchema<TState>,
  state: TState
): string[] {
  const requiredInvalidIds = schema.fields
    .filter((field) => !field.visibleWhen || field.visibleWhen(state))
    .filter((field) => !isFieldDisabled(field, state))
    .filter((field) => field.requiredFields && !isFieldComplete(field, state))
    .map((field) => field.id)

  const customInvalidIds = schema.validationErrorFieldIds?.(state) ?? []

  return [...new Set([...requiredInvalidIds, ...customInvalidIds])]
}

export function useSchemaWizard<TState extends WizardStateSchema>(
  schema: WizardSchema<TState>
): UseSchemaWizardReturn<TState> {
  const [state, setState] = useState<TState>(schema.stateSchema)
  const [validationAttempted, setValidationAttempted] = useState(false)
  const [invalidFieldIds, setInvalidFieldIds] = useState<string[]>([])

  const updateField = useCallback((fieldId: string, value: any) => {
    setState((prevState) => ({
      ...prevState,
      [fieldId]: value,
    }))
  }, [])

  const updateFields = useCallback((values: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...values,
    }))
  }, [])

  const resetWizard = useCallback(() => {
    setState(schema.stateSchema)
    setValidationAttempted(false)
    setInvalidFieldIds([])
  }, [schema.stateSchema])

  useEffect(() => {
    if (!validationAttempted) return
    setInvalidFieldIds(getInvalidFieldIds(schema, state))
  }, [schema, state, validationAttempted])

  const isFieldValid = useCallback(
    (fieldId: string): boolean => {
      const field = schema.fields.find((f) => f.id === fieldId)
      if (!field) return true
      return !field.requiredFields || isFieldComplete(field, state)
    },
    [schema.fields, state]
  )

  const isWizardValid = useCallback((): boolean => {
    const invalidIds = getInvalidFieldIds(schema, state)
    if (invalidIds.length > 0) return false
    return !schema.validate || schema.validate(state)
  }, [schema, state])

  const validateWithFeedback = useCallback((): WizardValidationResult => {
    setValidationAttempted(true)
    const ids = getInvalidFieldIds(schema, state)
    const schemaValid = !schema.validate || schema.validate(state)
    const isValid = ids.length === 0 && schemaValid

    setInvalidFieldIds(ids)

    return {
      isValid,
      invalidFieldIds: ids,
      firstInvalidFieldId: ids[0],
    }
  }, [schema, state])

  return {
    state,
    updateField,
    updateFields,
    resetWizard,
    isFieldValid,
    isWizardValid,
    validationAttempted,
    invalidFieldIds,
    validateWithFeedback,
  }
}
